<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- NAME -->
                    <div :class="{ 'col-lg-8': update, 'col-md-6': !update }">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}
                            </label>
                            <input v-model="consent.title" class="form-control shadow-none text-black" required
                                :class="[{ 'is-invalid': submitted && errors?.title }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.title ?? false">
                                <p v-for="error in errors.title" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- DESCRIPTION -->
                    <div :class="{ 'col-lg-8': update, 'col-md-6': !update }">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.description.label') }}
                            </label>
                            <textarea v-model="consent.description" class="form-control shadow-none text-black"
                                :class="[{ 'is-invalid': submitted && errors?.description }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.description ?? false">
                                <p v-for="error in errors.description" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{ error }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="col-12" v-if="loading">
                        <SpinnerLoading />
                    </div>
                    <div class="col-12" v-else>
                        <div class="d-flex align-items-center justify-content-between">
                            <!-- DELETE -->
                            <button v-if="update" type="button"
                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                                data-bs-target="#confirmModal">
                                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                <span class="position-relative">{{ $t('cta.delete_consent') }}</span>
                            </button>
                            <span v-else></span>
                            <!-- SAVE -->
                            <button
                                class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                type="submit" v-on:click="submitted = true">
                                {{ $t('cta.save_consent') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>
    <ConfirmModal @confirm="deleteConsent()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import { Consent } from "@/utils/models/consent.model";

export default defineComponent({
    name: "ConsentForm",
    components: {
        ConfirmModal,
        SpinnerLoading,
    },
    data: function () {
        return {
            consent: (this.$route.meta.consent ?? {}) as Consent,
            submitted: false,
            loading: false,
            errors: undefined as any,
        }
    },
    computed: {
        update(): boolean {
            return this.$route.meta.consent !== undefined && this.$route.meta.consent !== null;
        },
        has_versions(): boolean {
            return this.update === true && this.consent.last_consent_version !== undefined;
        }
    },
    methods: {
        submit(event): void {
            this.errors = undefined;
            event.preventDefault();
            console.log("CONSENT", this.consent);
            removeNullProperties<Consent>(this.consent);

            this.loading = true;
            if (this.update) {
                console.log('UPDATE');
                API.updateConsent(this.consent).then((response) => {
                    this.$router.push(`/consents`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                console.log('CREATE');

                API.createConsent(this.consent).then((response) => {
                    if (response.data?.data?.id !== undefined) {
                        this.$router.push(`/consent/${response.data.data.id}`);
                    } else {
                        this.$router.push(`/consents`);
                    }
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
        deleteConsent(): void {
            this.loading = true;
            API.deleteConsent(this.consent.id!).then((response) => {
                if (response.data.success === true) {
                    this.$router.push('/consents');
                }
            }).catch((error) => {
                this.showError();
            }).finally(() => {
                console.log("DELETED_CONSENT_ID", this.consent.id!);
                this.loading = false;
            })
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        }
    },
});
</script>